import { ThemeType } from '@festina-life/fplus-monaco'
import { useThemeContext } from '@festina-life/ui/theme'
import type { FileDiffMetadata, FileDiffOptions } from '@pierre/diffs'
import { FileDiff } from '@pierre/diffs/react'
import { type CSSProperties, useMemo } from 'react'

import { useAnnounceDiff } from '../../context/diffPresenceContext'
import { useFPlusDiffLayout } from './useFPlusDiffLayout'

type Props = {
  fileDiff: FileDiffMetadata
}

/**
 * Both F-plus themes are handed over at once and the renderer picks between
 * them, so switching light and dark does not re-highlight the diff.
 */
const themes = { light: ThemeType.light, dark: ThemeType.dark }

/**
 * The typography of the read-only viewer — `font-code text-sm leading-code` with
 * the editor's ligatures, as set in `FPlusViewer` — handed to the diff renderer
 * through the custom properties it reads. Both surfaces show the same
 * expressions, so an expression must not change shape depending on which one it
 * is shown on. Each value is the theme token the viewer itself resolves to,
 * rather than a copy of it. These properties inherit through the shadow root the
 * diff renders into.
 */
const viewerTypography = {
  '--diffs-font-family': 'var(--font-code)',
  '--diffs-font-size': 'var(--text-sm)',
  '--diffs-line-height': 'var(--leading-code)',
  '--diffs-font-features': "'liga', 'calt'",
} as CSSProperties

/**
 * A diff between two versions of the same value, shown with line numbers, in
 * the layout the reader chose.
 *
 * What the two sides are read as — F-plus, or the plain prose of a piece of
 * documentation — is decided by the model that built the diff, so every
 * comparison in the portal is read the same way whatever it holds.
 *
 * The file name each side carries is synthetic — it exists only so the language
 * is inferred — so the header that would show it is left off.
 */
export const DiffView = ({ fileDiff }: Props) => {
  const { isDarkMode } = useThemeContext()
  const { layout } = useFPlusDiffLayout()

  useAnnounceDiff()

  const options = useMemo<FileDiffOptions<undefined>>(
    () => ({
      theme: themes,
      themeType: isDarkMode ? 'dark' : 'light',
      diffStyle: layout,
      disableFileHeader: true,
    }),
    [isDarkMode, layout],
  )

  return (
    <div style={viewerTypography}>
      <FileDiff fileDiff={fileDiff} options={options} />
    </div>
  )
}
